import { useState, useEffect, useCallback } from "react";
import employeeService from "Services/AdminServices/EmployeeService";

export const PlacementOption = {
  SIBLING: "SIBLING",
  PARENT: "PARENT",
};

export const useReporting = (designationId = 0, departmentId = 0, initialData = {}) => {
  const [availableSupervisors, setAvailableSupervisors] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [placementOption, setPlacementOption] = useState("");
  const [supervisorDirectReports, setSupervisorDirectReports] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  // REMOVED: loadingDirectReports as it's not used in the UI
  const [validationMessage, setValidationMessage] = useState("");

  // Initialize with initialData if provided
  useEffect(() => {
    if (initialData?.selectedSupervisor) {
      setSelectedSupervisor(initialData.selectedSupervisor);
    }
    if (initialData?.placementOption) {
      setPlacementOption(initialData.placementOption);
    }
  }, [initialData]);

  // Fetch supervisors whenever designationId or departmentId changes
  useEffect(() => {
    // Allow fetching with just departmentId if designationId is 0
    if (!departmentId) return;

    const fetchSupervisors = async () => {
      setLoadingSupervisors(true);
      try {
  
        const data = await employeeService.getSupervisors(designationId, departmentId);
        setAvailableSupervisors(data || []);
        
        // If we have initial data and supervisors are loaded, set the selected supervisor
        if (initialData?.selectedSupervisor && data?.length > 0) {
          const supervisor = data.find(s => s.id === initialData.selectedSupervisor.id);
          if (supervisor) {
            setSelectedSupervisor(supervisor);
          }
        }
      } catch (err) {
        console.error("Error fetching supervisors", err);
        setAvailableSupervisors([]);
      } finally {
        setLoadingSupervisors(false);
      }
    };

    fetchSupervisors();
  }, [designationId, departmentId, initialData]);

  // Fetch direct reports whenever a supervisor is selected
  useEffect(() => {
    if (!selectedSupervisor?.id) {
      setSupervisorDirectReports([]);
      return;
    }

    const fetchDirectReports = async () => {
      // REMOVED: setLoadingDirectReports(true);
      try {
        const data = await employeeService.getDirectReports(selectedSupervisor.id);
        setSupervisorDirectReports(data || []);
      } catch (err) {
        console.error("Error fetching direct reports", err);
        setSupervisorDirectReports([]);
      } 
      
    };

    fetchDirectReports();
  }, [selectedSupervisor]);

  // Memoize the handler functions to prevent unnecessary re-renders
  const handleSupervisorChange = useCallback((supervisorId) => {
    const sup = availableSupervisors.find((s) => s.id === Number(supervisorId));
    setSelectedSupervisor(sup || null);
    setPlacementOption(""); // reset placement option
    setValidationMessage(""); // clear validation message
  }, [availableSupervisors]);

  const handlePlacementChange = useCallback((option) => {
    setPlacementOption(option);
    setValidationMessage(""); // clear validation message
  }, []);

  const validate = useCallback(() => {
    if (!selectedSupervisor) {
      setValidationMessage("Please select a supervisor before proceeding.");
      return false;
    }
    if (!placementOption) {
      setValidationMessage("Please select a placement option (Sibling/Parent).");
      return false;
    }
    setValidationMessage("");
    return true;
  }, [selectedSupervisor, placementOption]);

  // REMOVED: The entire resetState function as it was unused.

  return {
    availableSupervisors,
    selectedSupervisor,
    placementOption,
    supervisorDirectReports,
    loadingSupervisors,
    // REMOVED: loadingDirectReports from the return object
    validationMessage,
    handleSupervisorChange,
    handlePlacementChange,
    validate,
    // REMOVED: resetState from the return object
  };
};